import { Box } from '@mui/material';
import { actionMenuStyles } from 'apps/studio/studio-tab/components/actions-menu/ActionMenuStyles';
import { ReactComponent as Upscale8kIcon } from 'core/assets/svg/studio-tab/8k-upscale.svg';
import { ReactComponent as ChangeAnything } from 'core/assets/svg/studio-tab/change-anything.svg';
import { ReactComponent as ChangeClothingIcon } from 'core/assets/svg/studio-tab/change-cloth-icon.svg';
import { ReactComponent as FaceReplaceIcon } from 'core/assets/svg/studio-tab/face-replace.svg';
import { ReactComponent as FacelockIcon } from 'core/assets/svg/studio-tab/facelock.svg';
import { ReactComponent as ControlNetFacelockSection } from 'core/assets/svg/studio-tab/feed-prompts-modal/controlnet-facelock-section.svg';
import { ReactComponent as PoseControlIcon } from 'core/assets/svg/studio-tab/posecontrol.svg';
import { ReactComponent as ProductPhotoIcon } from 'core/assets/svg/studio-tab/product-photo.svg';
import { ReactComponent as TrueTouchIcon } from 'core/assets/svg/studio-tab/true-touch.svg';
import { ReactComponent as VideoAnyoneIcon } from 'core/assets/svg/studio-tab/video-anyone-icon.svg';
import BaseTypography from 'core/components/typography/BaseTypography';
import { featureConfig } from 'core/config/featureConfig';
import { LayoutOrientation } from 'core/constants/enums/LayoutOrientation.ts';
import { MediaTypeView } from 'core/constants/enums/MediaTypeView.ts';
import { StudioSidebarViews } from 'core/constants/enums/StudioSidebarViews.ts';
import { ConditionalLayer } from 'core/layouts/ui-blocks/ReusableComponents';
import { reusableStyles } from 'core/layouts/ui-blocks/ReusableStyles';
import { APP, HISTORY, TEXT_TO_IMAGE } from 'core/navigation/Routes';
import { uploadVideoAnyoneImageLinkAsync } from 'core/store/middleware/StudioCanvasMiddleware';
import {
	setRandomiseStudioPrompt,
	setStudioAppBarMediaType,
	setStudioClothingTargetImage,
	setStudioFaceReplaceImage,
	setStudioFaceReplaceImageId,
	setStudioProductPhotoBackgroundImage,
	setStudioSidebarView,
	setStudioUpscaleImage,
	uploadStudioChangeAnythingImageLinkAsync,
	uploadStudioControlNetImageLinkAsync,
	uploadStudioFaceLockImageLinkAsync,
	uploadStudioTrueTouchImageLinkAsync,
} from 'core/store/reducers/studioReducer/StudioSlice';
import { validateImageDimension } from 'core/utils/utils';
import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

const styles = actionMenuStyles;
const ACTION_BUTTON_TEXT_VARIANT = 'b1_regular';
const ACTION_BUTTON_TEXT_STYLES = { color: 'colors.light1' };

function ActionButton({ icon, text, onClick }) {
	return (
		<Box id={`action-menu-${text}-card`} sx={styles.actionMenuButtonStyles} onClick={onClick}>
			{icon}
			<BaseTypography variant={ACTION_BUTTON_TEXT_VARIANT} styles={ACTION_BUTTON_TEXT_STYLES}>
				{text}
			</BaseTypography>
		</Box>
	);
}

export default function ActionsMenu(props) {
	const { image, handleClose, orientation, source, imageId, width, height, aspectRatio } = props;
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const handleNavigationAndDisableRandomPrompt = useCallback(() => {
		if (source === HISTORY) {
			navigate(`/${APP}/${TEXT_TO_IMAGE}`);
			// NOTE: SETTING RANDOMISE STUDIO PROMPT TO FALSE
			dispatch(setRandomiseStudioPrompt(false));
		}
	}, [dispatch, navigate, source]);
	return (
		<Box
			id="use-image-button-section"
			sx={[
				styles.useImageButtonContainer,
				orientation === LayoutOrientation.HORIZONTAL && styles.horizontalLayoutContainer,
				orientation !== LayoutOrientation.HORIZONTAL && styles.verticalLayoutContainer,
			]}
		>
			<Box sx={[reusableStyles.flexCol, { gap: '8px', alignItems: 'start' }]}>
				<Box sx={[reusableStyles.flexRow, { gap: '8px', '& svg': { color: 'colors.light2' } }]}>
					<ConditionalLayer isShown={orientation === LayoutOrientation.HORIZONTAL}>
						<ControlNetFacelockSection />
					</ConditionalLayer>
					<BaseTypography variant="b2_caption_medium" styles={{ color: 'colors.light2' }}>
						Use this image in
					</BaseTypography>
				</Box>
				<ConditionalLayer isShown={orientation === LayoutOrientation.HORIZONTAL}>
					<BaseTypography variant="b2_caption" styles={{ color: 'colors.light4' }}>
						Edit the image or use it in new image generations
					</BaseTypography>
				</ConditionalLayer>
			</Box>
			<Box id="action-menu-buttons-cont" sx={styles.actionMenuButtonsContainer(orientation)}>
				<ActionButton
					icon={
						<VideoAnyoneIcon
							style={{
								width: '20px',
								height: '20px',
							}}
						/>
					}
					text="Convert to Video"
					onClick={(e) => {
						e.stopPropagation();
						handleClose();
						dispatch(setStudioAppBarMediaType(MediaTypeView.VIDEO));
						const isValid = validateImageDimension(height, width, aspectRatio);
						if (isValid) {
							dispatch(uploadVideoAnyoneImageLinkAsync(image, imageId, TEXT_TO_IMAGE));
						}
						handleNavigationAndDisableRandomPrompt();
					}}
				/>
				{featureConfig.isChangeItEnabled && (
					<ActionButton
						icon={<ChangeAnything />}
						text="Change It"
						onClick={(e) => {
							e.stopPropagation();
							handleClose();
							dispatch(setStudioAppBarMediaType(MediaTypeView.IMAGE));
							dispatch(uploadStudioChangeAnythingImageLinkAsync(image));
							dispatch(setStudioSidebarView(StudioSidebarViews.CHANGE_ANYTHING));
							handleNavigationAndDisableRandomPrompt();
						}}
					/>
				)}
				<ActionButton
					icon={<PoseControlIcon />}
					text="Pose Lock"
					onClick={(e) => {
						e.stopPropagation();
						handleClose();
						dispatch(setStudioAppBarMediaType(MediaTypeView.IMAGE));
						dispatch(uploadStudioControlNetImageLinkAsync(image));
						handleNavigationAndDisableRandomPrompt();
					}}
				/>
				<ActionButton
					icon={<FacelockIcon />}
					text="Face Lock"
					onClick={(e) => {
						e.stopPropagation();
						handleClose();
						dispatch(setStudioAppBarMediaType(MediaTypeView.IMAGE));
						dispatch(uploadStudioFaceLockImageLinkAsync(image));
						handleNavigationAndDisableRandomPrompt();
					}}
				/>
				{featureConfig.isTrueTouchEnabled && (
					<ActionButton
						icon={<TrueTouchIcon />}
						text="True Touch"
						onClick={(e) => {
							e.stopPropagation();
							handleClose();
							dispatch(setStudioAppBarMediaType(MediaTypeView.IMAGE));
							dispatch(uploadStudioTrueTouchImageLinkAsync(image));
							dispatch(setStudioSidebarView(StudioSidebarViews.TRUE_TOUCH));
							handleNavigationAndDisableRandomPrompt();
						}}
					/>
				)}
				<ActionButton
					icon={<Upscale8kIcon />}
					text="Upscale"
					onClick={(e) => {
						e.stopPropagation();
						handleClose();
						dispatch(setStudioAppBarMediaType(MediaTypeView.IMAGE));
						dispatch(setStudioUpscaleImage(image));
						dispatch(setStudioSidebarView(StudioSidebarViews.UPSCALE_8K));
						handleNavigationAndDisableRandomPrompt();
					}}
				/>
				<ActionButton
					icon={<FaceReplaceIcon />}
					text="Face Replace"
					onClick={(e) => {
						e.stopPropagation();
						handleClose();
						dispatch(setStudioAppBarMediaType(MediaTypeView.IMAGE));
						dispatch(setStudioFaceReplaceImage(image));
						dispatch(setStudioFaceReplaceImageId(imageId));
						dispatch(setStudioSidebarView(StudioSidebarViews.FACE_REPLACE));
						handleNavigationAndDisableRandomPrompt();
					}}
				/>
				<ActionButton
					icon={<ChangeClothingIcon />}
					text="Clothes Swap"
					onClick={(e) => {
						e.stopPropagation();
						handleClose();
						dispatch(setStudioAppBarMediaType(MediaTypeView.IMAGE));
						dispatch(setStudioClothingTargetImage({ id: imageId, url: image }));
						dispatch(setStudioSidebarView(StudioSidebarViews.CHANGE_CLOTHING));
						handleNavigationAndDisableRandomPrompt();
					}}
				/>
				<ActionButton
					icon={<ProductPhotoIcon />}
					text="Product Photo"
					onClick={(e) => {
						e.stopPropagation();
						handleClose();
						dispatch(setStudioAppBarMediaType(MediaTypeView.IMAGE));
						dispatch(setStudioProductPhotoBackgroundImage({ id: imageId, url: image }));
						dispatch(setStudioSidebarView(StudioSidebarViews.PRODUCT_PHOTO));
						handleNavigationAndDisableRandomPrompt();
					}}
				/>
			</Box>
		</Box>
	);
}
